"use client";

import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { UseFormReturn } from "react-hook-form";
import axios from "axios";
import { ConfigFormRenderer } from "@/components/Form/ConfigFormRenderer";
import { useSpotUser } from "@/utils/spotUser";
import { InboxUseCase } from "../types";
import { StepLayout } from "./HowItWorksSteps";

interface ContextStepProps {
  useCase: InboxUseCase;
  form: UseFormReturn<Record<string, any>>;
  configData: any;
  onConfigDataChange: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
  canProceed: boolean;
}

export const ContextStep = ({
  useCase,
  form,
  configData,
  onConfigDataChange,
  onNext,
  onBack,
  canProceed,
}: ContextStepProps) => {
  const { spotGroup } = useSpotUser();
  const previousValuesRef = useRef<string>(JSON.stringify(form.getValues()));

  const formValues = form.watch();

  // Debounced config refresh on form changes
  useEffect(() => {
    if (!spotGroup?.id || !useCase) return;

    const currentValuesStr = JSON.stringify(formValues);
    if (currentValuesStr === previousValuesRef.current) return;
    previousValuesRef.current = currentValuesStr;

    const timeoutId = setTimeout(() => {
      axios
        .post(
          `/api/backend/groups/${spotGroup.id}/inbox-templates/${useCase}/config`,
          formValues
        )
        .then((response) => {
          onConfigDataChange(response.data);
        });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formValues, spotGroup?.id, useCase, onConfigDataChange]);

  return (
    <StepLayout
      onNext={onNext}
      onBack={onBack}
      disabled={!canProceed}
      nextLabel="Continue"
    >
      <div className="text-center space-y-2 mb-5">
        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="text-lg font-medium text-foreground"
        >
          Customize your inbox
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="text-sm text-muted-foreground"
        >
          Configure how your inbox handles incoming cases
        </motion.p>
      </div>

      <div className="w-full max-w-lg mb-2">
        {configData?.config?.root ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <ConfigFormRenderer
              config={configData.config.root}
              form={form}
              flattenAccordions
            />
          </motion.div>
        ) : (
          <div className="min-h-[200px] flex items-center justify-center border border-dashed border-border rounded-xl bg-zinc-50/50">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xs text-muted-foreground/60"
            >
              No configuration available
            </motion.p>
          </div>
        )}
      </div>
    </StepLayout>
  );
};
